import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, Row, Tooltip } from 'antd';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { PercentageInput } from 'app/components/commons/PercentageInput/PercentageInput';
import { SwitchableInput } from 'app/components/commons/SwitchableInput/SwitchableInput';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import type { BulkEditFormValues } from 'app/typings/bulkEdit';
import type { ExtranetPackage } from 'app/typings/packages';
import { formatDateWithoutHour } from 'app/utils/dates';
import { computeCrossRate, isValidDiscount } from 'app/utils/price';

import type {
  FieldsDisplayValues,
  PackageDisplayValues,
  RoomDisplayValues,
} from '../BulkEdit';

type DiscountRateSwitchProps = {
  openValues: FieldsDisplayValues;
  formValues: BulkEditFormValues;
  initialDiscountRate?: number;
  toggleSwitch: (
    switchValue: boolean,
    section: keyof PackageDisplayValues | keyof RoomDisplayValues,
    packageId?: number
  ) => void;
  pkg: ExtranetPackage;
};

export const DiscountRateSwitch = ({
  openValues,
  formValues,
  initialDiscountRate,
  toggleSwitch,
  pkg,
}: DiscountRateSwitchProps) => {
  const { currencyFormatter } = useContext(CurrencyContext);
  const { getExperiencesForPackage } = useContext(LegacyHotelContext);

  const isSwitchOn = !!openValues.packageDisplayValues.find(
    (packageValue) => packageValue.packageId === pkg.id
  )?.discountRate;

  const experiences = getExperiencesForPackage(pkg.id);

  const price = computeCrossRate(
    pkg.id,
    experiences,
    formatDateWithoutHour(formValues.startDate ?? new Date()),
    !!pkg?.dayPackage,
    0
  );

  return (
    <SwitchableInput
      name={`discountRate-switch-${pkg.id}`}
      value={isSwitchOn}
      onChange={(value: boolean) => {
        toggleSwitch(value, 'discountRate', pkg.id);
      }}
      title={<h3>{pkg.aliasForHotel}</h3>}
      childNode={
        <>
          <Row align={'middle'} className="switchable-input-name">
            <div>Staycation discount</div>
            {!pkg.dayPackage && (
              <Tooltip
                title={
                  <>
                    <div>The minimum Staycation discount is 18%.</div>
                    <div>
                      Staycation rate = BAR + Package price (
                      {currencyFormatter(price)}) - Staycation discount
                    </div>
                  </>
                }
                placement="top"
                overlayClassName="bulkEdit__tooltip"
              >
                <InfoCircleOutlined color="#BBB" />
              </Tooltip>
            )}
          </Row>
          <div className="inputContainer">
            <Form.Item
              name={[`${pkg.id}`, 'discountRate']}
              initialValue={initialDiscountRate}
              dependencies={['bar']}
              rules={[
                {
                  validator() {
                    if (!isSwitchOn) {
                      return Promise.resolve('');
                    }

                    if (
                      formValues?.[pkg.id]?.discountRate === null ||
                      formValues?.[pkg.id]?.discountRate === undefined ||
                      !isValidDiscount(
                        -(formValues?.[pkg.id]?.discountRate ?? 0),
                        pkg.dayPackage
                      )
                    ) {
                      return Promise.reject(
                        'Staycation rate must be between -18% and -70% of crossed rate'
                      );
                    }

                    return Promise.resolve('');
                  },
                },
              ]}
              noStyle={false}
            >
              <PercentageInput
                disabled={!isSwitchOn}
                min={0}
                placeholder="Discount"
                onWheel={(e) => e.currentTarget.blur()}
              />
            </Form.Item>
          </div>
        </>
      }
    />
  );
};
